import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";

import type { ContractTermsResponse } from "@bw/shared/types/generated";
import { formatDateUtc, formatPercent } from "@bw/shared/utils";

export type DetailReadState = "loading" | "error" | "ready";

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

function exactPercent(value: ContractTermsResponse["commission_rate"]) {
  return formatPercent(value, 4)
    .replace(/(\.\d*?)0+%$/, "$1%")
    .replace(/\.%$/, "%");
}

export function OrgCommercialTermsPanel({
  orgId,
  terms,
  state,
  onRetry,
}: {
  orgId: string;
  terms: ContractTermsResponse | null | undefined;
  state: DetailReadState;
  onRetry: () => void;
}) {
  const futureOnly = Boolean(
    terms?.version_id && terms.version_id === terms.upcoming_version_id,
  );
  const scheduled = Boolean(
    terms?.upcoming_version_id ??
      terms?.upcoming_version_number ??
      terms?.upcoming_effective_date,
  );
  const scheduledVersion =
    terms?.upcoming_version_number ??
    (futureOnly ? terms?.version_number : null);
  const scheduledEffectiveDate =
    terms?.upcoming_effective_date ??
    (futureOnly ? terms?.effective_date : null);

  return (
    <Paper
      component="section"
      aria-labelledby="commercial-terms-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 } }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            alignItems: { sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <Typography
            id="commercial-terms-heading"
            component="h2"
            variant="h6"
            sx={{ fontWeight: 700 }}
          >
            Commercial Terms
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <Button
              component={RouterLink}
              to={`/organizations/${orgId}/contract-terms`}
              size="small"
              variant="contained"
            >
              Publish custom terms
            </Button>
            <Button
              component={RouterLink}
              to={`/organizations/${orgId}/contract-versions`}
              size="small"
              variant="outlined"
            >
              View history
            </Button>
          </Stack>
        </Stack>

        {state === "loading" ? (
          <Box
            role="status"
            aria-label="Loading commercial terms"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 4 }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : state === "error" ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            Commercial terms could not be loaded. Try again.
          </Alert>
        ) : !terms ? (
          <Typography color="text.secondary" variant="body2">
            No commercial terms version is on file.
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            {futureOnly ? (
              <Typography color="text.secondary" variant="body2">
                No current commercial terms are in effect.
              </Typography>
            ) : (
              <Box
                component="dl"
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  m: 0,
                }}
              >
                <Fact
                  label="Current version"
                  value={
                    terms.version_number == null
                      ? "Legacy"
                      : `v${terms.version_number}`
                  }
                />
                <Fact
                  label="Commission"
                  value={exactPercent(terms.commission_rate)}
                />
                <Fact
                  label="Current effective date"
                  value={
                    terms.effective_date
                      ? formatDateUtc(terms.effective_date)
                      : "Not set"
                  }
                />
                <Fact
                  label="Payout schedule"
                  value={
                    terms.payout_schedule?.frequency?.replace(/_/g, " ") ??
                    "Not set"
                  }
                />
                <Fact
                  label="Business acceptance"
                  value={
                    terms.accepted_at
                      ? formatDateUtc(terms.accepted_at)
                      : "Not accepted"
                  }
                />
                <Fact
                  label="Reacceptance"
                  value={
                    terms.requires_reacceptance ? "Required" : "Not required"
                  }
                />
                <Fact
                  label="Termination date"
                  value={
                    terms.termination_date
                      ? formatDateUtc(terms.termination_date)
                      : "Open-ended"
                  }
                />
              </Box>
            )}

            {scheduled ? (
              <Box
                component="dl"
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  m: 0,
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Fact
                  label="Scheduled version"
                  value={
                    scheduledVersion == null
                      ? "Not set"
                      : `v${scheduledVersion}`
                  }
                />
                <Fact
                  label="Scheduled effective date"
                  value={
                    scheduledEffectiveDate
                      ? formatDateUtc(scheduledEffectiveDate)
                      : "Not set"
                  }
                />
                {futureOnly && (
                  <>
                    <Fact
                      label="Scheduled commission"
                      value={exactPercent(terms.commission_rate)}
                    />
                    <Fact
                      label="Scheduled payout schedule"
                      value={
                        terms.payout_schedule?.frequency?.replace(/_/g, " ") ??
                        "Not set"
                      }
                    />
                  </>
                )}
              </Box>
            ) : (
              <Typography color="text.secondary" variant="body2">
                No scheduled change.
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
